import { Link } from "react-router-dom";
import { Coffee, Droplets, Zap, Leaf, Check, ArrowRight } from "lucide-react";

const DrinkMachines = () => {
  const features = [
    "Up to 400 beverage selections",
    "Energy-efficient LED lighting",
    "Temperature-controlled compartments",
    "Digital payment ready",
    "ADA compliant options",
    "Remote monitoring included",
  ];

  return (
    <div className="min-h-screen" data-testid="drink-machines-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-navy relative overflow-hidden" data-testid="drink-hero">
        <div className="absolute top-10 right-10 w-8 h-8 bg-gold rotate-12 opacity-60" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-gold text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
                DRINK MACHINES
              </span>
              <h1 className="font-heading text-4xl md:text-5xl text-cream mb-4">
                Cold Drinks, <span className="text-coral">Happy Teams</span>
              </h1>
              <p className="text-cream/80 text-lg mb-8">
                From refreshing waters to energizing sodas, our drink machines
                keep your space hydrated and satisfied around the clock.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="drink-cta"
              >
                Get a Drink Machine <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="bg-coral/20 rounded-2xl p-8 border-2 border-cream/20">
                <Coffee className="h-32 w-32 text-cream mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beverage Types */}
      <section className="py-20 md:py-32" data-testid="beverage-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">
              Beverages for Every Taste
            </h2>
            <p className="text-navy/70 max-w-2xl mx-auto">
              We stock our machines based on what your users actually want,
              ensuring fresh, popular selections every time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Water & Sports */}
            <div className="card-retro" data-testid="beverage-type-water">
              <div className="bg-gold/30 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <Droplets className="h-7 w-7 text-navy" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Water & Sports Drinks</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Bottled Water
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Flavored Water
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Gatorade
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Powerade
                </li>
              </ul>
            </div>

            {/* Sodas & Energy */}
            <div className="card-retro" data-testid="beverage-type-soda">
              <div className="bg-gold/30 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <Coffee className="h-7 w-7 text-navy" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Sodas & Energy</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Coca-Cola
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Pepsi
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Red Bull
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Monster
                </li>
              </ul>
            </div>

            {/* Coffee & Tea */}
            <div className="card-retro" data-testid="beverage-type-coffee">
              <div className="bg-gold/30 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <Zap className="h-7 w-7 text-navy" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Coffee & Tea</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Cold Brew
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Iced Coffee
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Bottled Tea
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Kombucha
                </li>
              </ul>
            </div>

            {/* Healthy Options */}
            <div className="card-retro" data-testid="beverage-type-healthy">
              <div className="bg-gold/30 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                <Leaf className="h-7 w-7 text-navy" />
              </div>
              <h3 className="font-heading text-xl text-navy mb-4">Healthy Options</h3>
              <ul className="space-y-2">
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Fresh Juices
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Smoothies
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Protein Drinks
                </li>
                <li className="text-navy/70 text-sm flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-coral rounded-full" />Organic Options
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32 bg-gold/20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-cream text-navy font-mono text-sm px-4 py-2 rounded-full border-2 border-navy mb-6">
                MACHINE FEATURES
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-navy mb-6">
                Modern Machines, Modern Experience
              </h2>
              <ul className="space-y-4">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="bg-matcha w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="h-4 w-4 text-cream" />
                    </div>
                    <span className="text-navy">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border-2 border-navy p-8 shadow-[8px_8px_0px_0px_#E07A5F]">
              <div className="text-center">
                <div className="font-mono text-coral text-sm mb-2">CAPACITY</div>
                <div className="font-heading text-5xl text-navy mb-4">400+</div>
                <p className="text-navy/70">Beverage selections available</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-coral" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-cream mb-4">
            Ready for Refreshment?
          </h2>
          <p className="text-cream/90 mb-8 max-w-xl mx-auto">
            Get a zero-cost drink machine for your space today.
          </p>
          <Link
            to="/contact"
            className="btn-secondary inline-block"
            data-testid="cta-button"
          >
            Request Your Machine
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DrinkMachines;
